import { useState, useCallback, useEffect } from 'react';
import { message, Modal } from 'antd';
import axios from '../utility/axios';

export function filterContacts(contactList, searchText) {
    if (!contactList || contactList.length === 0) return [];
    let filtered = contactList;
    if (searchText && searchText.trim() !== '') {
        const lowerCaseSearch = searchText.toLowerCase();
        filtered = contactList.filter(contact => {
            if (!contact) return false;
            const email = contact.email?.toLowerCase() || '';
            const name = `${contact.group_name || ''} ${contact.firstname || ''} ${contact.lastname || ''}`.trim().toLowerCase();
            const phone = contact.phone?.toLowerCase() || '';
            return email.includes(lowerCaseSearch) || name.includes(lowerCaseSearch) || phone.includes(lowerCaseSearch);
        });
    }
    return [...filtered].sort((a, b) => {
        const aFav = a.contact_type === 'favorite' ? 0 : 1;
        const bFav = b.contact_type === 'favorite' ? 0 : 1;
        return aFav - bFav;
    });
}

export function formatContactDisplay(contact) {
    if (!contact) return '';
    if (contact._type === 'group') {
        return contact.group_name || contact.firstname || '';
    }
    if (contact.phone && contact.phone.trim()) {
        return `${contact.email || ''} - ${contact.phone.trim()}`;
    }
    return contact.email || '';
}

export function getGroupMemberCount(contact) {
    if (!contact || contact._type !== 'group' || !contact.email) return 0;
    return contact.email.split(';').map(e => e.trim()).filter(e => e !== '').length;
}

export function getGroupMembers(contact) {
    if (!contact || contact._type !== 'group' || !contact.email) return [];
    return contact.email.split(';').map(e => e.trim()).filter(e => e !== '');
}

export function useComposeContacts({ apiEndpoint, isLoggedIn, authLoading, t }) {
    const [contacts, setContacts] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [contactEditModalVisible, setContactEditModalVisible] = useState(false);
    const [contactEditingContact, setContactEditingContact] = useState(null);

    const fetchContacts = useCallback(async () => {
        if (!isLoggedIn) return;
        try {
            setContactsLoading(true);
            const response = await axios.get(apiEndpoint);
            const contactsList = (response.data.contacts || []).map(c => ({ ...c, _type: 'contact' }));
            const groupsList = (response.data.groups || []).map(g => ({ ...g, _type: 'group', firstname: g.group_name || '' }));
            setContacts([...contactsList, ...groupsList]);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        } finally {
            setContactsLoading(false);
        }
    }, [isLoggedIn, apiEndpoint]);

    useEffect(() => {
        if (!authLoading && isLoggedIn) {
            fetchContacts();
        }
    }, [isLoggedIn, authLoading, fetchContacts]);

    const updateContactsApi = (payload) => axios.post('/api/profile/contacts/update/', payload);

    const handleContactAddFavorite = async (contact, e) => {
        e?.stopPropagation();
        if (!contact?.id) return;
        try {
            await updateContactsApi({ ids: [contact.id], action: 'add_favorite' });
            setContacts(prev => prev.map(c => (c.id === contact.id && c._type === contact._type) ? { ...c, contact_type: 'favorite' } : c));
            message.success(t('contacts.addedToFavorites'));
        } catch (error) {
            message.error(t('contacts.failedToAddFavorite'));
        }
    };

    const handleContactRemoveFavorite = async (contact, e) => {
        e?.stopPropagation();
        if (!contact?.id) return;
        try {
            await updateContactsApi({ ids: [contact.id], action: 'remove_favorite' });
            const newType = contact._type === 'group' ? 'groups' : 'recents';
            setContacts(prev => prev.map(c => (c.id === contact.id && c._type === contact._type) ? { ...c, contact_type: newType } : c));
            message.success(t('contacts.removedFromFavorites'));
        } catch (error) {
            message.error(t('contacts.failedToRemoveFavorite'));
        }
    };

    const handleContactEdit = (contact, e) => {
        e?.stopPropagation();
        if (!contact?.id) return;
        setContactEditingContact(contact);
        setContactEditModalVisible(true);
    };

    const handleContactDelete = (contact, e) => {
        e?.stopPropagation();
        if (!contact?.id) return;
        Modal.confirm({
            title: t('contacts.deleteContact'),
            content: t('contacts.confirmDelete'),
            okText: t('contacts.delete'),
            okType: 'danger',
            cancelText: t('contacts.cancel'),
            onOk: async () => {
                try {
                    await updateContactsApi({ ids: [contact.id], action: 'delete' });
                    setContacts(prev => prev.filter(c => !(c.id === contact.id && c._type === contact._type)));
                    message.success(t('contacts.contactMoved'));
                } catch (error) {
                    message.error(t('contacts.failedToDelete'));
                }
            }
        });
    };

    const handleContactEditModalClose = () => {
        setContactEditModalVisible(false);
        setContactEditingContact(null);
    };

    const handleContactEditModalSaved = () => {
        fetchContacts();
    };

    return {
        contacts,
        contactsLoading,
        setContacts,
        fetchContacts,
        updateContactsApi,
        contactEditModalVisible,
        contactEditingContact,
        handleContactAddFavorite,
        handleContactRemoveFavorite,
        handleContactEdit,
        handleContactDelete,
        handleContactEditModalClose,
        handleContactEditModalSaved,
    };
}
